import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Home, Wrench, UtensilsCrossed, Sparkles, ArrowRight, Phone, Menu, X, Scissors, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

/**
 * LocalHeader - Navigation header for the /local/a/* subdomain pages
 * Only visible on local business pages, separate from the main B2B site navigation
 */
const LocalHeader = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: "/local/a", label: "Home", icon: Home },
    { href: "/local/a/services", label: "Services", icon: Sparkles },
    { href: "/local/a/pricing", label: "Pricing", icon: null },
    { href: "/local/a/trades", label: "Trades", icon: Wrench },
    { href: "/local/a/restaurants", label: "Restaurants", icon: UtensilsCrossed },
    { href: "/local/a/salons", label: "Salons", icon: Scissors },
    { href: "/local/a/medical", label: "Medical", icon: Stethoscope },
  ];

  const isActive = (href: string) => {
    if (href === "/local/a") {
      return location.pathname === "/local/a";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/local/a" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">VGP</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-foreground">VGP</span>
              <span className="text-muted-foreground ml-1 text-sm">Local</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isActive(link.href)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="flex items-center gap-2">
            <a 
              href="tel:+447857450763"
              className="hidden sm:flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden lg:inline">0785 745 0763</span>
            </a>
            <Button size="sm" variant="premium" asChild className="hidden sm:flex">
              <Link to="/local/a/get-started" className="flex items-center gap-1">
                Get Quote <ArrowRight className="w-3 h-3" />
              </Link>
            </Button>
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border py-4">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2",
                    isActive(link.href)
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-border my-2" />
              <Link
                to="/local/a/get-started"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg bg-primary text-white text-sm font-medium text-center"
              >
                Get Your Free Quote
              </Link>
              <a
                href="tel:+447857450763"
                className="px-4 py-3 text-sm text-muted-foreground flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                0785 745 0763
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default LocalHeader;